import type { Mode } from '@/types'
import * as Sentry from '@sentry/vue'
import isWebview from 'is-ua-webview'
import type { App } from 'vue'
import { isAxiosError } from 'axios'

export class NonActionableWarning extends Error {
    constructor(message: string) {
        super(message)
    }
}

function isActionable(error: unknown) {
    if (error instanceof NonActionableWarning) {
        return false
    }
    if (isAxiosError(error)) {
        const status = error.response?.status ?? 0
        return status >= 500
    }

    return true
}

export function setupSentry(app: App) {
    const hostname = window.location.hostname

    Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: hostname.includes('.staging.') || import.meta.env.DEV ? 'staging' : 'production',
        tracesSampleRate: 2.0,
        enabled: import.meta.env.MODE !== 'development',
        // debug: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error', 'trace'] }),
        ],
        enableLogs: true,
        beforeSend: (event, hint) => {
            // prevent event on localhost
            if (hostname === 'localhost') {
                return null
            }

            if (!isActionable(hint.originalException)) {
                event.level = 'warning'
            }

            return event
        },
    })
}

export function updateSentryContext(params: {
    partyId: string
    workspaceId: string
    workflowDefinitionId: string
    version?: number
    mode: Mode
    workflowId?: string
}) {
    const { partyId, workspaceId, workflowDefinitionId, version, mode, workflowId } = params
    Sentry.setUser({ id: partyId })
    Sentry.setTag('workspace_id', workspaceId)
    Sentry.setTag('workflow_definition_id', workflowDefinitionId)
    Sentry.setTag('is_webview', isWebview(window.navigator.userAgent))
    Sentry.setTag('token_version', version)
    Sentry.setTag('workflow_mode', mode)
    Sentry.setTag('workflow_id', workflowId)
}
